import type { SceneKey } from "../b2c/timing";
import type { Lang } from "../catalog";

/**
 * Instagram reels for Frak's account (9:16, silent: the sound is added in
 * the app). Every entry is a composition in Studio's `Reels` folder and an
 * output of the video server (`reels/<id>.mp4` + `reels/<id>-cover.jpg`,
 * frame 0): add a reel here and both pick it up.
 *
 * Two kinds:
 *   scenes  a cut of B2C scenes (`ReelVideo`), same beat grid as the B2C video
 *   reel    a reel of its own, in `src/reels/<reel>/` (the folder is the
 *           key: the video server fingerprints that folder's import
 *           graph), mapped to its component in `components.ts`
 *
 * Plain data (types only): Bun loads it outside the bundle. It lives here
 * rather than in `src/catalog.ts`, which every video depends on: adding a
 * reel must not re-render the B2C, B2B and merchant videos.
 */
export const REELS = [
  // "Tu recommandes déjà" → you earn 0 € → with Frak, up to X € → CTA (~13s)
  {
    id: "Reel-FR-Gratis",
    name: "Gratis",
    lang: "fr",
    scenes: ["hook", "unpaid", "cta"],
  },
  // The receipt of your unpaid recommendations.
  { id: "Reel-FR-Ticket", name: "Le ticket", lang: "fr", reel: "ticket" },
  // Trading cards of everyday influencers; the last one is you.
  { id: "Reel-FR-Cards", name: "Les cartes", lang: "fr", reel: "cards" },
  // Quiz: the FAQ objections as VRAI / FAUX, the last one true.
  { id: "Reel-FR-Quiz", name: "Vrai ou faux", lang: "fr", reel: "quiz" },
  // Split screen: same reco, sans Frak vs avec Frak.
  {
    id: "Reel-FR-Split",
    name: "Même reco, deux fins",
    lang: "fr",
    reel: "split",
  },
  // Slot machine of the live brands and their rewards.
  {
    id: "Reel-FR-Slots",
    name: "La machine à sous",
    lang: "fr",
    reel: "slots",
  },
  // First successful referral of the brand Loulenn: 30 € / 10 €.
  { id: "Reel-FR-Loulenn", name: "Loulenn", lang: "fr", reel: "loulenn" },
  // Counter of the live brands (from the snapshot), their logos dropping in.
  { id: "Reel-FR-Marques", name: "Les marques", lang: "fr", reel: "brands" },
  // Halloween: a haunted bank screen melts and falls apart, the Frak logo re-forms it and credits it.
  { id: "Reel-FR-Halloween", name: "Halloween", lang: "fr", reel: "halloween" },
] as const satisfies readonly Reel[];

export type Reel = {
  /** Composition id, `Reel-<LANG>-<Name>`. */
  id: string;
  /** Shown in the video server's dashboard. */
  name: string;
  lang: Lang;
} & ({ scenes: readonly SceneKey[] } | { reel: string });

/** Keys of the reels with their own folder. */
export type ReelKey = Extract<(typeof REELS)[number], { reel: string }>["reel"];
